import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Divider,
  Grid,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert as MuiAlertComponent,
  Snackbar,
  Card,
  CardContent,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import ShareIcon from '@mui/icons-material/Share';
import PeopleIcon from '@mui/icons-material/People';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import { motion, AnimatePresence } from 'framer-motion';
import api, { ENABLE_MOCK_DATA } from '../services/api';
import MuiAlert from '@mui/material/Alert';
import TransactionRecord from './TransactionRecord';
import { useNavigate } from 'react-router-dom';

// 主题色配置（与Dashboard一致，使用蓝色）
const COLORS = {
  primary: '#3b82f6',
  secondary: '#06b6d4',
  accent: '#06b6d4',
  text: '#f1f5f9',
  textMuted: '#94a3b8',
  bgDark: '#0a0f1a',
  bgCard: 'rgba(15, 23, 42, 0.98)',
  bgCardHover: 'rgba(20, 30, 60, 0.98)',
  glow: 'rgba(59, 130, 246, 0.35)',
  border: 'rgba(59, 130, 246, 0.15)',
  gold: '#ffd700',
  goldBg: 'rgba(255, 215, 0, 0.08)',
  goldBorder: 'rgba(255, 215, 0, 0.2)',
};

// 动画配置
const pageVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.3, ease: 'easeOut' },
  },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

// Alert组件
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// 一个简单而稳定的TabPanel组件，避免不必要的重新渲染
const TabPanel = (props) => {
  const { children, value, index } = props;
  
  return (
    <div
      role="tabpanel"
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      hidden={value !== index}
      style={{ padding: '0px' }}
    >
      {children}
    </div>
  );
};

const formatInviteNumber = (value) => {
  if (value === null || value === undefined) {
    return '0';
  }
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return `${value}`;
  }
  return numericValue.toLocaleString();
};

const inviteFontSizeConfigs = [
  {
    minLength: 11,
    normal: {
      xs: 'clamp(1rem, 6vw, 1.6rem)',
      sm: 'clamp(1.2rem, 4vw, 1.9rem)',
      md: 'clamp(1.3rem, 3vw, 2.1rem)',
      xl: 'clamp(1.4rem, 2vw, 2.4rem)'
    },
    small: {
      xs: 'clamp(0.95rem, 5.5vw, 1.4rem)',
      sm: 'clamp(1.05rem, 3.6vw, 1.7rem)',
      md: 'clamp(1.15rem, 2.6vw, 1.9rem)',
      xl: 'clamp(1.25rem, 1.8vw, 2.1rem)'
    }
  },
  {
    minLength: 9,
    normal: {
      xs: 'clamp(1.1rem, 6vw, 1.8rem)',
      sm: 'clamp(1.3rem, 4.2vw, 2.1rem)',
      md: 'clamp(1.5rem, 3vw, 2.4rem)',
      xl: 'clamp(1.7rem, 2vw, 2.6rem)'
    },
    small: {
      xs: 'clamp(1rem, 5.5vw, 1.6rem)',
      sm: 'clamp(1.2rem, 3.6vw, 1.9rem)',
      md: 'clamp(1.35rem, 2.6vw, 2.1rem)',
      xl: 'clamp(1.5rem, 1.8vw, 2.3rem)'
    }
  },
  {
    minLength: 7,
    normal: {
      xs: 'clamp(1.2rem, 6vw, 2rem)',
      sm: 'clamp(1.4rem, 4vw, 2.3rem)',
      md: 'clamp(1.6rem, 3vw, 2.6rem)',
      xl: 'clamp(1.9rem, 2vw, 2.8rem)'
    },
    small: {
      xs: 'clamp(1.1rem, 5.5vw, 1.8rem)',
      sm: 'clamp(1.25rem, 3.6vw, 2.1rem)',
      md: 'clamp(1.45rem, 2.6vw, 2.3rem)',
      xl: 'clamp(1.65rem, 1.8vw, 2.5rem)'
    }
  },
  {
    minLength: 0,
    normal: {
      xs: 'clamp(1.3rem, 6vw, 2.2rem)',
      sm: 'clamp(1.5rem, 4vw, 2.5rem)',
      md: 'clamp(1.8rem, 3vw, 2.8rem)',
      xl: 'clamp(2rem, 2vw, 3rem)'
    },
    small: {
      xs: 'clamp(1.15rem, 5.5vw, 1.9rem)',
      sm: 'clamp(1.35rem, 3.6vw, 2.2rem)',
      md: 'clamp(1.55rem, 2.6vw, 2.5rem)',
      xl: 'clamp(1.75rem, 1.8vw, 2.7rem)'
    }
  }
];

const getInviteStatFontSize = (formattedValue = '0', size = 'normal') => {
  const numericLength = formattedValue.replace(/[^\d]/g, '').length || 1;

  const config =
    inviteFontSizeConfigs.find(({ minLength }) => numericLength >= minLength) ||
    inviteFontSizeConfigs[inviteFontSizeConfigs.length - 1];

  return size === 'small' ? config.small : config.normal;
};

const InviteStatValue = ({ value, suffix, size = 'normal' }) => {
  const formattedValue = formatInviteNumber(value);
  const fontSize = getInviteStatFontSize(formattedValue, size);

  return (
    <Typography
      variant="h4"
      sx={{
        color: 'COLORS.primary',
        display: 'inline-flex',
        alignItems: 'baseline',
        justifyContent: 'center',
        gap: 0.5,
        width: '100%',
        lineHeight: 1.05,
        whiteSpace: 'nowrap',
        fontSize,
        fontFamily: '"Roboto Mono","SFMono-Regular",Menlo,Monaco,Consolas,"Liberation Mono","Courier New",monospace',
        letterSpacing: '0.02em'
      }}
    >
      <Box
        component="span"
        sx={{
          fontVariantNumeric: 'tabular-nums',
          minWidth: 0,
          flexShrink: 1,
          whiteSpace: 'nowrap'
        }}
      >
        {formattedValue}
      </Box>
      {suffix ? (
        <Box
          component="span"
          sx={{
            fontSize: '0.55em',
            flexShrink: 0
          }}
        >
          {suffix}
        </Box>
      ) : null}
    </Typography>
  );
};

const UserCenter = ({ initialTab = 0, onClose }) => {
  // 状态变量
  const [tabValue, setTabValue] = useState(initialTab);
  const [userInfo, setUserInfo] = useState(null);
  const [userInfoLoaded, setUserInfoLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userInfoLoading, setUserInfoLoading] = useState(false);
  const [cardKey, setCardKey] = useState('');
  const [rechargeHistory, setRechargeHistory] = useState([]);
  const [error, setError] = useState('');
  const [rechargeSuccess, setRechargeSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [transactionType, setTransactionType] = useState('all');
  const [page, setPage] = useState(1);
  const [transactionsPerPage, setTransactionsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [snackbarKey, setSnackbarKey] = useState(0); // 用于强制重新渲染 Snackbar
  const [rechargeLoading, setRechargeLoading] = useState(false);
  // 客服联系方式弹窗
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [serviceWechatId, setServiceWechatId] = useState('');
  // 新增：记录上次充值的卡密，用于跟踪防止重复提交
  const [lastRechargeCardKey, setLastRechargeCardKey] = useState('');
  // 新增：记录充值前余额，用于后续检测是否充值成功
  const [preRechargeBalance, setPreRechargeBalance] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // 密码修改相关状态
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // 邀请相关状态
  const [inviteCode, setInviteCode] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [inviteStats, setInviteStats] = useState(null);
  const [inviteList, setInviteList] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [invitePage, setInvitePage] = useState(1);
  const [inviteTotalPages, setInviteTotalPages] = useState(1);

  const navigate = useNavigate();

  // 加载用户信息
  const fetchUserInfo = async (options = {}) => {
    try {
      setUserInfoLoading(true);
      console.log('UserCenter: 开始获取用户信息');
      const response = await api.getUserInfo();
      console.log('UserCenter: 用户信息响应数据:', response.data);

      // 处理不同的响应格式
      let userData = response.data;
      if (userData.success && userData.user) {
        console.log('UserCenter: 检测到嵌套格式的响应，提取user对象:', userData.user);
        userData = userData.user;
      }

      // 确保用户数据有效
      if (userData && typeof userData === 'object') {
        console.log('UserCenter: 设置用户信息，余额:', userData.balance);
        setUserInfo(userData);
        setUserInfoLoaded(true);
        
        // 更新本地存储
        localStorage.setItem('user', JSON.stringify(userData));
        
        // 获取请求来源
        const source = options.source || '';
        
        // 触发用户数据更新事件，通知其他组件
        window.dispatchEvent(new CustomEvent('userDataUpdated', {
          detail: { 
            userData: userData,
            source: source || 'user-center-refresh'
          }
        }));
      } else {
        console.error('UserCenter: API返回了无效的用户数据格式:', userData);
        // 尝试从本地存储获取
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const storedUserData = JSON.parse(storedUser);
            setUserInfo(storedUserData);
            console.log('UserCenter: 从本地存储加载了用户信息:', storedUserData);
          } catch (e) {
            console.error('UserCenter: 解析本地存储的用户数据失败:', e);
            setSnackbar({
              open: true,
              message: '加载用户信息失败，请刷新页面重试',
              severity: 'error'
            });
          }
        } else {
          setSnackbar({
            open: true,
            message: '无法获取用户信息，请登录',
            severity: 'error'
          });
        }
      }
    } catch (error) {
      console.error('UserCenter: 获取用户信息失败:', error);
      // 尝试从本地存储获取信息
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const storedUserData = JSON.parse(storedUser);
          setUserInfo(storedUserData);
          console.log('UserCenter: 从本地存储加载了用户信息:', storedUserData);
        } catch (e) {
          console.error('UserCenter: 解析本地存储的用户数据失败:', e);
        }
      }
      
      // 只有在不需要抑制错误通知时才显示
      if (!options.suppressNotification) {
        setSnackbar({
          open: true,
          message: '获取用户信息失败，请刷新页面重试',
          severity: 'error'
        });
      }
    } finally {
      setUserInfoLoading(false);
    }
  };

  // 获取充值记录
  const fetchRechargeHistory = async () => {
    // 注意：不在这里设置setRechargeLoading(true)，避免每次都清空UI状态
    try {
      console.log('正在获取充值记录...');
      const response = await api.getUserRecharges();
      
      if (response && response.data) {
        console.log('获取充值记录成功，原始数据:', response.data);
        
        // 处理不同格式的响应数据
        let recharges = response.data;
        
        // 如果是对象且包含transactions属性，则取该属性
        if (typeof response.data === 'object' && !Array.isArray(response.data) && response.data.transactions) {
          console.log('检测到transactions属性，提取数据:', response.data.transactions);
          recharges = response.data.transactions;
        }
        
        // 确保recharges是数组
        if (!Array.isArray(recharges)) {
          console.warn('充值记录不是数组格式，转换为数组:', recharges);
          recharges = recharges ? [recharges] : [];
        }
        
        console.log('处理后的充值记录:', recharges);
        setRechargeHistory(recharges);
        
        // 重置加载错误
        setError('');
      } else {
        console.warn('获取充值记录响应为空或格式不正确');
        setRechargeHistory([]);
      }
    } catch (error) {
      console.error('获取充值记录失败:', error);
      
      // 只在严重错误时显示提示，避免遮挡充值结果信息
      if (error.message && error.message.includes('critical')) {
        setSnackbar({
          open: true,
          message: error.userMessage || error.serverMessage || '获取充值记录失败，请稍后再试',
          severity: 'error'
        });
      }
    } finally {
      setRechargeLoading(false);
    }
  };

  // 获取邀请数据
  const fetchInviteData = async (page = invitePage) => {
    setInviteLoading(true);
    try {
      // 获取邀请码
      const codeResponse = await api.getMyInviteCode();
      if (codeResponse.data && codeResponse.data.success) {
        setInviteCode(codeResponse.data.inviteCode || '');
        setInviteLink(codeResponse.data.inviteLink || '');
      }
      
      // 获取邀请统计
      const statsResponse = await api.getInviteStats();
      if (statsResponse.data && statsResponse.data.success) {
        setInviteStats(statsResponse.data.stats);
      }
      
      // 获取邀请列表
      const listResponse = await api.getInviteList({ page, limit: 20 });
      if (listResponse.data && listResponse.data.success) {
        setInviteList(listResponse.data.data || []);
        if (listResponse.data.pagination) {
          setInviteTotalPages(listResponse.data.pagination.pages || 1);
        }
      }
    } catch (error) {
      console.error('获取邀请数据失败:', error);
      setSnackbar({
        open: true,
        message: '获取邀请数据失败，请稍后再试',
        severity: 'error'
      });
    } finally {
      setInviteLoading(false);
    }
  };
  
  // 回退复制方法 - 使用临时文本区域
  const fallbackCopyToClipboard = (text) => {
    try {
      // 创建临时文本区域
      const textArea = document.createElement('textarea');
      textArea.value = text;
      
      // 避免滚动到底部
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      // 执行复制命令
      const successful = document.execCommand('copy');
      if (successful) {
        console.log('使用回退方法复制成功');
        // 清理
        document.body.removeChild(textArea);
        return true;
      } else {
        console.error('回退复制失败');
        // 清理
        document.body.removeChild(textArea);
        return false;
      }
    } catch (err) {
      console.error('回退复制方法失败:', err);
      return false;
    }
  };
  
  // 安全复制到剪贴板函数（带回退方法）
  const safeCopyToClipboard = (text) => {
    try {
      // 检查 navigator.clipboard API 是否可用
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        // 现代浏览器 API
        navigator.clipboard.writeText(text)
          .then(() => {
            console.log('文本已复制到剪贴板');
          })
          .catch(err => {
            console.error('无法复制文本: ', err);
            // 如果现代API失败，使用回退方法
            fallbackCopyToClipboard(text);
          });
        return true;
      } else {
        // 回退方法
        return fallbackCopyToClipboard(text);
      }
    } catch (err) {
      console.error('复制失败:', err);
      // 如果出错，使用回退方法
      return fallbackCopyToClipboard(text);
    }
  };
  
  // 复制邀请链接
  const copyInviteLink = () => {
    if (inviteLink) {
      safeCopyToClipboard(inviteLink);
      setSnackbar({
        open: true,
        message: '邀请链接已复制到剪贴板',
        severity: 'success'
      });
    }
  };
  
  // 复制邀请码
  const copyInviteCode = () => {
    if (inviteCode) {
      safeCopyToClipboard(inviteCode);
      setSnackbar({
        open: true,
        message: '邀请码已复制到剪贴板',
        severity: 'success'
      });
    }
  };

  // 获取交易记录
  const loadTransactions = async () => {
    setTransactionLoading(true);
    
    try {
      console.log('正在获取交易记录...');
      // 调用API获取交易记录，确保获取所有类型包括消费记录
      const response = await api.getUserTransactions();
      
      // 处理响应数据
      if (response && response.data) {
        console.log('获取交易记录成功，原始数据:', response.data);
        let data = response.data;
        
        // 处理嵌套数据结构
        if (response.data.data && Array.isArray(response.data.data)) {
          data = response.data.data;
        }
        
        // 确保data是数组
        if (!Array.isArray(data)) {
          data = [data];
          console.warn('交易记录不是数组格式，转换为数组:', data);
        }
        
        // 设置交易记录
        console.log('处理后的交易记录:', data);
        setTransactions(data);
      } else {
        console.warn('获取交易记录响应为空或格式不正确');
        setTransactions([]);
      }
    } catch (error) {
      console.error('获取交易记录失败:', error);
      setTransactions([]);
      
      // 只在严重错误时显示提示，避免遮挡充值结果信息
      if (error.message && error.message.includes('critical')) {
        setSnackbar({
          open: true,
          message: error.userMessage || error.serverMessage || '获取交易记录失败，请稍后再试',
          severity: 'error'
        });
      }
    } finally {
      setTransactionLoading(false);
    }
  };

  // 初始化加载 - 无闪烁版本
  useEffect(() => {
    // 1. 立即显示本地缓存（如果有）
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const cachedUser = JSON.parse(storedUser);
        setUserInfo(cachedUser);
        setUserInfoLoaded(true);
        console.log('从缓存加载用户信息:', cachedUser);
      } catch (e) {
        console.error('解析缓存失败:', e);
      }
    }
    
    // 2. 后台静默获取最新数据
    const loadFromServer = async () => {
      try {
        const response = await api.getUserInfo();
        let userData = response.data;
        if (userData.success && userData.user) userData = userData.user;
        
        if (userData && userData.id) {
          setUserInfo(userData);
          setUserInfoLoaded(true);
          localStorage.setItem('user', JSON.stringify(userData));
          window.dispatchEvent(new CustomEvent('userDataUpdated', {
            detail: { userData, source: 'user-center-init' }
          }));
        }
        
        // 预加载当前Tab数据
        if (tabValue === 1 && rechargeHistory.length === 0) {
          fetchRechargeHistory();
        } else if (tabValue === 2 && transactions.length === 0) {
          loadTransactions();
        } else if (tabValue === 3 && !inviteCode) {
          fetchInviteData();
        }
      } catch (error) {
        console.error('从服务器加载失败:', error);
      }
    };
    
    loadFromServer();
    
    return () => {};
    
    // 添加全局错误处理器，捕获未处理的Promise拒绝
    const handleUnhandledRejection = (event) => {
      console.error('未处理的Promise拒绝:', event.reason);
      // 防止显示过多错误
      if (!snackbar.open) {
        setSnackbar({
          open: true,
          message: '操作失败，请稍后再试',
          severity: 'error'
        });
      }
      // 防止默认处理（控制台错误）
      event.preventDefault();
    };
    
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    // 清理函数
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // 外部可能通过initialTab属性传入初始标签页
  useEffect(() => {
    setTabValue(initialTab);
  }, [initialTab]);

  // 根据标签页加载数据（仅在Tab切换时触发）
  useEffect(() => {
    let isActive = true;
    let timeoutId = null;
    
    const loadData = async () => {
      if (!isActive) return;
      
      try {
        if (tabValue === 2 && transactions.length === 0) {
          console.log('开始加载交易记录，当前记录数:', transactions?.length || 0);
          setTransactionLoading(true);
          await loadTransactions();
        } else if (tabValue === 1 && rechargeHistory.length === 0) {
          console.log('开始加载充值记录，当前记录数:', rechargeHistory?.length || 0);
          setRechargeLoading(true);
          await fetchRechargeHistory();
        } else if (tabValue === 3 && !inviteCode) {
          console.log('开始加载邀请数据...');
          await fetchInviteData();
        }
      } catch (error) {
        console.error(`标签页 ${tabValue} 数据加载失败:`, error);
        setError(`加载数据失败: ${error.userMessage || error.message || '请稍后再试'}`);
      } finally {
        setTransactionLoading(false);
        setRechargeLoading(false);
      }
    };
    
    timeoutId = setTimeout(loadData, 100);
    
    return () => {
      isActive = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [tabValue]);

  // 全局错误处理
  useEffect(() => {
    // 全局捕获未处理的Promise异常
    const handleUnhandledRejection = (event) => {
      console.error('未捕获的Promise异常:', event.reason);
      
      // 显示通用错误消息
      setSnackbar({
        open: true,
        message: '操作失败，请稍后再试',
        severity: 'error'
      });
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    // 组件卸载时清理事件监听
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // 处理标签页切换
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // 处理充值响应
  const handleRechargeResponse = (response) => {
    // 确保响应有效
    if (!response || !response.data) {
      console.error('无效的充值响应');
      setSnackbar(prev => ({
        ...prev,
        open: true,
        message: '充值处理失败，请联系客服',
        severity: 'error',
        autoHideDuration: 5000
      }));
      return;
    }
    
    const responseData = response.data;
    console.log('处理充值响应:', responseData);
    
    // 卡密不存在
    if (responseData.message && responseData.message.includes('不存在')) {
      setSnackbar(prev => ({
        ...prev,
        open: true,
        message: '卡密不存在或已被使用',
        severity: 'error',
        autoHideDuration: 5000
      }));
      return;
    }
    
    // 测试卡重复使用（优先检查，因为消息也包含"已使用"或"使用过"）
    if (responseData.message && responseData.message.includes('测试卡') &&
        (responseData.message.includes('已使用') || responseData.message.includes('使用过'))) {
      console.log('检测到测试卡重复使用:', responseData.message);
      const errorMessage = responseData.message || '您已经使用过测试卡，每个账号只能使用一次测试卡';
      console.log('设置错误提示:', errorMessage);
      // 使用 key 强制重新渲染 Snackbar，确保能正确显示
      setSnackbarKey(prev => prev + 1);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
        autoHideDuration: 8000 // 较长的显示时间，让用户看清楚
      });
      console.log('Snackbar状态已设置，应该显示弹窗');
      return;
    }

    // 体验卡相关限制（已购买体验卡后无法使用测试卡，或反过来）
    if (responseData.message && responseData.message.includes('体验卡')) {
      console.log('检测到体验卡相关错误:', responseData.message);
      setSnackbarKey(prev => prev + 1);
      setSnackbar({
        open: true,
        message: responseData.message,
        severity: 'error',
        autoHideDuration: 8000
      });
      return;
    }

    // 卡密已使用
    if (responseData.message && responseData.message.includes('已使用')) {
      setSnackbar(prev => ({
        ...prev,
        open: true,
        message: '该卡密已被使用，请使用新的卡密',
        severity: 'error',
        autoHideDuration: 5000
      }));
      return;
    }
    
    // 充值成功
    if (responseData.success || responseData.message && responseData.message.includes('成功')) {
      // 提取充值金额和当前余额
      const amount = responseData.amount || responseData.rechargeAmount || '未知';
      
      // 使用更清晰的成功消息，不再显示当前余额
      setSnackbar(prev => ({
        ...prev,
        open: true,
        message: `充值成功！余额增加了${amount}`,
        severity: 'success',
        autoHideDuration: 8000 // 更长的显示时间
      }));
      
      // 更新充值记录
      fetchRechargeHistory();
      return;
    }
    
    // 其他情况，可能是未知响应格式
    // 如果有错误消息，优先显示错误消息
    if (responseData.message) {
      // 检查是否是错误消息（success为false或errorType存在）
      const isError = responseData.success === false || responseData.errorType;
    console.warn('未能识别的充值响应格式:', responseData);
      setSnackbar(prev => ({
        ...prev,
      open: true,
        message: responseData.message,
        severity: isError ? 'error' : 'info',
        autoHideDuration: isError ? 8000 : 5000
      }));
    } else {
      console.warn('未能识别的充值响应格式:', responseData);
      setSnackbar(prev => ({
        ...prev,
        open: true,
        message: '充值请求已处理，请刷新查看最新余额',
      severity: 'info',
      autoHideDuration: 5000
      }));
    }
  };

  // 处理充值
  const handleRecharge = async () => {
    if (!cardKey.trim()) {
      setSnackbar({
        open: true,
        message: '请输入卡密',
        severity: 'warning',
        autoHideDuration: 3000
      });
      return;
    }

    // 设置提交状态，防止重复提交
    setSubmitting(true);

    try {
      console.log('提交充值请求:', cardKey);
      
      const response = await api.recharge(cardKey);
      console.log('充值响应:', response);
      
      // 处理充值响应（会在内部设置正确的提示）
      const responseData = response?.data || {};
      const isError = responseData && (
        responseData.success === false || 
        responseData.errorType ||
        (responseData.message && (
          responseData.message.includes('不存在') ||
          responseData.message.includes('已使用') ||
          responseData.message.includes('使用过')
        ))
      );
      
      // 直接处理充值响应，它会设置正确的提示（包括400错误）
      handleRechargeResponse(response);
      
      // 如果是错误，提前返回，不执行后续刷新
      if (isError) {
        setCardKey(''); // 清空卡密输入
        setSubmitting(false);
        return;
      }
      
      // 充值后清空卡密输入
      setCardKey('');
      
    } catch (error) {
      console.error('充值请求失败（网络错误或其他异常）:', error);
      
      // catch 块只处理真正的网络错误或其他异常，不处理400等业务错误
      // 业务错误（如400）应该由 api.recharge 返回并在 handleRechargeResponse 中处理
      // 这里只处理网络错误、超时等真正的异常
      if (error.response && error.response.status) {
        // 如果 api.recharge 没有正确处理错误，这里作为备用处理
        // 但正常情况下不应该到达这里
        console.warn('异常：业务错误应该在 handleRechargeResponse 中处理', error);
        handleRechargeResponse({
          status: error.response.status,
          data: {
            success: false,
            message: error.response.data?.message || '充值失败，请稍后再试',
            errorType: error.response.status === 400 ? 'invalid_request' : 'server_error'
          }
        });
      } else {
        // 真正的网络错误
        setSnackbarKey(prev => prev + 1);
      setSnackbar({
        open: true,
          message: '网络连接失败，请检查网络后重试',
        severity: 'error',
          autoHideDuration: 8000
      });
      }
      
      // 延迟重置状态，让用户有足够时间看到错误消息
      setTimeout(() => {
        setSubmitting(false);
      }, 2000);
      
      return; // 出错时提前返回
    }
    
    // 充值成功时的处理
    try {
      // 刷新用户信息，但禁止显示更新提示
      await fetchUserInfo({ suppressNotification: true });
      
      // 刷新充值记录
      await fetchRechargeHistory();
    } catch (refreshError) {
      console.error('充值后刷新信息失败:', refreshError);
    } finally {
      setSubmitting(false);
    }
  };
  
  // 客服购买按钮点击
  const handleContactService = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/recharge/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.wechat) {
        setServiceWechatId(data.wechat);
      } else {
        setServiceWechatId('暂未设置客服微信号');
      }
    } catch (error) {
      setServiceWechatId('暂未设置客服微信号');
    }
    setServiceDialogOpen(true);
  };

  // 复制微信号
  const copyWechatId = () => {
    navigator.clipboard.writeText(serviceWechatId);
    setSnackbar({
      open: true,
      message: '微信号已复制到剪贴板',
      severity: 'success',
      autoHideDuration: 3000
    });
  };

  // 验证充值结果
  const verifyRechargeResult = async () => {
    console.log('验证充值结果...');

    try {
      // 获取用户当前信息
      const userResponse = await api.getUserInfo();
      
      if (!userResponse || !userResponse.data) {
        console.error('验证充值结果: 无法获取用户信息');
        return { 
          success: false, 
          message: '无法验证充值结果，请刷新页面查看最新余额' 
        };
      }
      
      const userData = userResponse.data;
      console.log('验证充值结果: 获取到用户数据', userData);
      
      // 构造模拟响应
      const simulatedResponse = {
        data: {
          success: true,
          message: '充值验证成功',
          amount: userData.balance - (userInfo?.balance || 0),
          currentBalance: userData.balance
        }
      };
      
      console.log('验证充值结果: 构造验证响应', simulatedResponse);
      
      // 如果余额没有变化，可能充值未成功
      if (userData.balance === (userInfo?.balance || 0)) {
        console.warn('验证充值结果: 余额未变化');
        
        return { 
          success: false, 
          message: '充值可能未成功，余额未发生变化',
          response: {
            data: {
              success: false,
              message: '充值验证失败，余额未变化'
            }
          }
        };
      }
      
      return { 
        success: true, 
        response: simulatedResponse 
      };
      
    } catch (error) {
      console.error('验证充值结果失败:', error);
      return { 
        success: false, 
        message: '验证充值结果时发生错误，请刷新页面检查余额',
        error
      };
    }
  };

  // 处理分页变化
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // 关闭Snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // 打开修改密码对话框

  const handleOpenPasswordDialog = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setOpenPasswordDialog(true);
  };

  // 修改密码
  const handleChangePassword = async () => {
    // 验证输入
    if (!oldPassword) {
      setSnackbar({
        open: true,
        message: '请输入当前密码',
        severity: 'error'
      });
      return;
    }

    if (!newPassword) {
      setSnackbar({
        open: true,
        message: '请输入新密码',
        severity: 'error'
      });
      return;
    }

    if (newPassword.length < 6) {
      setSnackbar({
        open: true,
        message: '新密码长度至少为6位',
        severity: 'error'
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setSnackbar({
        open: true,
        message: '两次输入的新密码不一致',
        severity: 'error'
      });
      return;
    }

    if (oldPassword === newPassword) {
      setSnackbar({
        open: true,
        message: '新密码不能与当前密码相同',
        severity: 'error'
      });
      return;
    }

    try {
      const response = await api.changePassword(oldPassword, newPassword);
      console.log('修改密码响应:', response);
      
      setSnackbar({
        open: true,
        message: '密码修改成功',
        severity: 'success'
      });
      
      // 关闭对话框并清空表单
      setOpenPasswordDialog(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
    } catch (error) {
      console.error('修改密码失败:', error);
      
      // 处理不同的错误情况，提供更详细的提示
      let errorMessage = '修改密码失败，请稍后重试';
      
      if (error.response) {
        // 服务器返回了错误响应
        if (error.response.status === 404) {
          errorMessage = '服务暂时不可用，请联系管理员';
        } else if (error.response.status === 400 && error.response.data?.message) {
          // 处理具体的业务错误
          const serverMessage = error.response.data.message;
          if (serverMessage.includes('旧密码') || serverMessage.includes('当前密码')) {
            errorMessage = '当前密码输入错误，请重新输入';
          } else if (serverMessage.includes('长度')) {
            errorMessage = '新密码长度必须至少6位字符';
          } else if (serverMessage.includes('相同')) {
            errorMessage = '新密码不能与当前密码相同';
          } else {
            errorMessage = serverMessage;
          }
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        // 网络请求失败
        errorMessage = '网络连接失败，请检查网络后重试';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  // 过滤交易记录
  const filteredTransactions = transactions
    ? transactions.filter(transaction => {
        if (transactionType === 'all') return true;
        if (transactionType === 'consumption' && (transaction.type === 'consumption' || transaction.type === 'consume')) {
          return true;
        }
        return transaction.type === transactionType;
      })
    : [];

  // 当前页显示的交易记录
  const displayedTransactions = filteredTransactions.slice(
    (page - 1) * transactionsPerPage, 
    page * transactionsPerPage
  );

  // 处理交易类型变化
  useEffect(() => {
    setPage(1);
  }, [transactionType]);

  // 加载状态展示
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <CircularProgress sx={{ color: COLORS.primary }} />
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '100%',
      maxWidth: { xs: '100%', md: '100%' },
      mx: 0,
      px: { xs: 0, md: 0 },
      mt: { xs: 0, md: 0 },
      overflow: 'hidden'
    }}>
        {/* 头部返回按钮区域 */}
        {onClose && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            mb: 2,
            borderRadius: '12px',
            bgcolor: COLORS.bgCard,
            border: `1px solid ${COLORS.border}`,
          }}>
            <IconButton
              onClick={onClose}
              sx={{
                color: COLORS.primary,
                '&:hover': {
                  bgcolor: `${COLORS.primary}15`,
                  color: COLORS.secondary
                },
                minWidth: { xs: '48px', sm: '40px' },
                minHeight: { xs: '48px', sm: '40px' },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography 
            variant="h6" 
            sx={{ 
              color: '#fff', 
              fontWeight: 'bold',
              display: { xs: 'block', sm: 'block' },
              textAlign: 'center',
              flex: 1
            }}
          >
            个人中心
          </Typography>
          {/* 占位元素保持居中 */}
          <Box sx={{ width: { xs: '48px', sm: '40px' } }} />
        </Box>
      )}
      
      <Box sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: COLORS.bgCard,
        borderRadius: { xs: 0, md: '12px 12px 0 0' },
        border: `1px solid ${COLORS.border}`,
        borderBottom: 'none',
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              color: COLORS.textMuted,
              fontWeight: 500,
              fontSize: { xs: '0.75rem', md: '0.95rem' },
              minHeight: { xs: 52, md: 60 },
              '& .MuiTab-iconWrapper': {
                fontSize: { xs: '1.1rem', md: '1.4rem' }
              }
            },
            '& .Mui-selected': {
              color: COLORS.primary,
            },
          }}
        >
          <Tab 
            icon={<AccountCircleIcon />} 
            label="账户" 
            iconPosition="start"
          />
          <Tab 
            icon={<MonetizationOnIcon />} 
            label="充值" 
            iconPosition="start"
          />
          <Tab 
            icon={<ReceiptIcon />} 
            label="记录" 
            iconPosition="start"
          />
          <Tab 
            icon={<PeopleIcon />} 
            label="邀请" 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      <Box sx={{ bgcolor: 'rgba(0, 30, 60, 0.8)', px: { xs: 0, sm: 0 } }}>
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 900, mx: 'auto' }}>
            {userInfo ? (
              <Box sx={{ background: 'linear-gradient(135deg, rgba(0,20,50,0.9) 0%, rgba(0,10,30,0.95) 100%)', borderRadius: 3, p: { xs: 2, md: 3 }, border: '1px solid rgba(0,255,159,0.2)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar sx={{ width: { xs: 56, md: 72 }, height: { xs: 56, md: 72 }, background: 'linear-gradient(135deg, #00d4aa 0%, #00a8cc 100%)', border: '3px solid rgba(0,255,159,0.5)', boxShadow: '0 0 20px rgba(0,255,159,0.3)' }}>
                    <AccountCircleIcon sx={{ fontSize: { xs: 32, md: 40 }, color: '#fff' }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: { xs: '1.2rem', md: '1.4rem' }, mb: 0.5 }}>{userInfo.username}</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: { xs: '0.75rem', md: '0.85rem' } }}>ID: {userInfo.id} · {new Date(userInfo.createdAt).toLocaleDateString()}</Typography>
                  </Box>
                  <Button variant="outlined" className="workspace-btn-neon-cyan" size="small" onClick={fetchUserInfo} disabled={userInfoLoading} startIcon={userInfoLoading ? <CircularProgress size={14} /> : <RefreshIcon />} sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' } }}>刷新</Button>
                </Box>
                <Box sx={{ textAlign: 'center', py: { xs: 3, md: 4 }, mb: 3, borderRadius: 2, bgcolor: 'rgba(0,255,159,0.03)', border: '1px solid rgba(0,255,159,0.08)' }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: { xs: '0.8rem', md: '0.9rem' }, mb: 1 }}>当前余额</Typography>
                  <Typography sx={{ color: '#00ff9f', fontWeight: 'bold', fontSize: { xs: '2.5rem', md: '3.5rem' }, lineHeight: 1, textShadow: '0 0 30px rgba(0,255,159,0.5)' }}>{userInfo.balance?.toLocaleString() || 0}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: { xs: '1rem', md: '1.2rem' } }}>字</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button variant="outlined" className="workspace-btn-neon-cyan" onClick={() => setTabValue(1)} sx={{ flex: 1, py: { xs: 1.25, md: 1.5 }, fontSize: { xs: '0.95rem', md: '1rem' }, fontWeight: 'bold' }}>立即充值</Button>
                  <Button variant="outlined" className="workspace-btn-neon-cyan" startIcon={<LockIcon />} onClick={handleOpenPasswordDialog} sx={{ flex: 1, py: { xs: 1.25, md: 1.5 }, fontSize: { xs: '0.95rem', md: '1rem' }, fontWeight: 'bold' }}>修改密码</Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 3 }}>
                <CircularProgress sx={{ color: '#00ff9f', mb: 2 }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>正在加载账户信息...</Typography>
              </Box>
            )}
          </Box>
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ 
            p: { xs: 0.5, sm: 2, md: 3 }, 
            maxWidth: 900, 
            mx: 'auto',
            '& > * + *': { mt: { xs: 1, sm: 2 } }
          }}>
            {/* 简洁标题栏 */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, px: { xs: 1, sm: 0 } }}>
              <Typography variant="h6" sx={{ color: 'COLORS.primary', fontWeight: 'bold', fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
                充值中心
              </Typography>
            </Box>

              {/* 卡密充值 */}
              <Box sx={{
                bgcolor: 'rgba(0, 0, 0, 0.3)',
                borderRadius: 2,
                p: { xs: 1.5, sm: 2 },
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 'bold', mb: 1.5, fontSize: { xs: '0.85rem', sm: '0.9rem' }, display: 'flex', alignItems: 'center' }}>
                  <CreditCardIcon sx={{ mr: 0.5, fontSize: '1rem' }} /> 卡密充值
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 1.5, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {error}
                  </Alert>
                )}

                {rechargeSuccess && (
                  <Alert severity="success" sx={{ mb: 1.5, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {successMessage}
                  </Alert>
                )}

                {/* 第一行：卡号输入 */}
                <TextField
                  fullWidth
                  label="输入卡密"
                  variant="outlined"
                  className="cyber-input"
                  placeholder="XXXX-XXXX-XXXX-XXXX"
                  value={cardKey}
                  id="recharge-card-key-input"
                  onChange={(e) => setCardKey(e.target.value)}
                  size="small"
                  sx={{ mb: 1.5, '& .MuiInputBase-input': { fontSize: { xs: '0.85rem', sm: '0.9rem' } } }}
                />

                {/* 第二行：立即充值 */}
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleRecharge}
                  disabled={rechargeLoading}
                  className="workspace-btn-neon-cyan"
                  sx={{
                    fontSize: '0.95rem !important',
                    py: '12px !important',
                    px: '24px !important',
                    fontWeight: '600 !important',
                    mb: 1.5,
                  }}
                >
                  {rechargeLoading ? (
                    <><CircularProgress size={20} sx={{ mr: 1, color: '#001e3c' }} />处理中...</>
                  ) : '立即充值'}
                </Button>

                {/* 客服购买按钮 */}
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleContactService}
                  startIcon={<HeadsetMicIcon sx={{ fontSize: 16 }} />}
                  className="workspace-btn-neon-cyan"
                  sx={{
                    fontSize: '0.95rem !important',
                    py: '12px !important',
                    px: '24px !important',
                    fontWeight: '600 !important',
                    mb: 1.5,
                  }}
                >
                  客服购买卡号
                </Button>

                <Typography variant="body2" sx={{ mb: 1.5, color: 'rgba(255, 255, 255, 0.4)', textAlign: 'center', fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                  每个卡密仅能使用一次
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.3)', px: 1 }}>或</Typography>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                </Box>

                {/* 第三行：微信扫码充值（绿色高亮） */}
                <Button
                  fullWidth
                  onClick={() => navigate('/recharge')}
                  sx={{
                    py: '14px !important',
                    px: '24px !important',
                    fontSize: '1rem !important',
                    fontWeight: '700 !important',
                    color: '#ffffff !important',
                    background: 'linear-gradient(135deg, #07c160 0%, #04a046 50%, #07c160 100%) !important',
                    border: '1px solid rgba(7, 193, 96, 0.6) !important',
                    borderRadius: '8px !important',
                    boxShadow: '0 0 20px rgba(7, 193, 96, 0.4), 0 0 40px rgba(7, 193, 96, 0.15), inset 0 1px 0 rgba(255,255,255,0.15) !important',
                    letterSpacing: '0.5px !important',
                    transition: 'all 0.25s ease !important',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #06ad56 0%, #03913c 50%, #06ad56 100%) !important',
                      boxShadow: '0 0 28px rgba(7, 193, 96, 0.55), 0 0 56px rgba(7, 193, 96, 0.2), inset 0 1px 0 rgba(255,255,255,0.2) !important',
                      transform: 'translateY(-1px) !important',
                    },
                    '&:active': {
                      transform: 'translateY(0) !important',
                    }
                  }}
                >
                  扫码充值
                </Button>
              </Box>

            {/* 充值记录 */}
            <Box sx={{ 
              bgcolor: 'rgba(0, 0, 0, 0.3)', 
              borderRadius: 2, 
              p: { xs: 1.5, sm: 2 },
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 'bold', mb: 1.5, fontSize: { xs: '0.85rem', sm: '0.9rem' }, display: 'flex', alignItems: 'center' }}>
                <ReceiptIcon sx={{ mr: 0.5, fontSize: '1rem' }} /> 充值记录
              </Typography>
              
              {rechargeLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={28} sx={{ color: 'COLORS.primary' }} />
                </Box>
              ) : rechargeHistory && rechargeHistory.length > 0 ? (
                <Box sx={{ overflow: 'auto', maxHeight: { xs: 300, sm: 400 } }}>
                  <Table sx={{ '& .MuiTableCell-root': { borderColor: 'rgba(255, 255, 255, 0.1)', color: 'white', py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 1.5 } } }}>
                    <TableHead sx={{ backgroundColor: 'rgba(0, 255, 159, 0.1)' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', color: 'COLORS.primary', fontSize: { xs: '0.75rem', sm: '0.8rem' }, whiteSpace: 'nowrap' }}>卡密</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: 'COLORS.primary', fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>金额</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: 'COLORS.primary', fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>日期</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rechargeHistory.map((recharge) => (
                        <TableRow key={recharge.id || recharge._id} hover>
                          <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: { xs: 100, sm: 'none' } }}>
                            {recharge.cardKey || recharge.metadata?.cardKey || '未知'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: 'COLORS.primary', fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                            +{parseFloat(recharge.amount || 0).toLocaleString()}
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, whiteSpace: 'nowrap' }}>
                            {recharge.createdAt ? new Date(recharge.createdAt).toLocaleDateString() : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3, color: 'rgba(255, 255, 255, 0.5)' }}>
                  <MonetizationOnIcon sx={{ fontSize: 36, color: 'rgba(255, 255, 255, 0.2)', mb: 1 }} />
                  <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>暂无充值记录</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ 
            p: { xs: 0.5, sm: 2 }, 
            maxWidth: 1400, 
            mx: 'auto',
            '& > * + *': { mt: { xs: 1, sm: 2 } }
          }}>
            {/* 标题栏 */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, px: { xs: 1, sm: 0 } }}>
              <Typography variant="h6" sx={{ color: 'COLORS.primary', fontWeight: 'bold', fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
                交易记录
              </Typography>
              <Button
                variant="outlined"
                className="workspace-btn-neon-cyan"
                size="small"
                onClick={loadTransactions}
                disabled={transactionLoading}
                startIcon={transactionLoading ? <CircularProgress size={14} /> : <RefreshIcon />}
                sx={{ minWidth: 'auto', px: { xs: 1, sm: 1.5 }, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
              >
                刷新
              </Button>
            </Box>

            {/* 筛选标签 */}
            <Box sx={{ display: 'flex', gap: 0.5, mb: 1, px: { xs: 1, sm: 0 }, flexWrap: 'wrap' }}>
              {[
                { value: 'all', label: '全部' },
                { value: 'recharge', label: '充值' },
                { value: 'consumption', label: '消费' },
                { value: 'invite_reward', label: '邀请' }
              ].map(item => (
                <Chip
                  key={item.value}
                  label={item.label}
                  size="small"
                  onClick={() => setTransactionType(item.value)}
                  sx={{
                    bgcolor: transactionType === item.value ? 'rgba(0, 255, 159, 0.2)' : 'rgba(255,255,255,0.05)',
                    color: transactionType === item.value ? '#00ff9f' : 'rgba(255,255,255,0.7)',
                    border: '1px solid',
                    borderColor: transactionType === item.value ? 'rgba(0, 255, 159, 0.5)' : 'rgba(255,255,255,0.1)',
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    height: { xs: 26, sm: 28 },
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'rgba(0, 255, 159, 0.15)'
                    }
                  }}
                />
              ))}
            </Box>

            {/* 交易列表 */}
            {transactionLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} sx={{ color: 'COLORS.primary' }} />
              </Box>
            ) : transactions && transactions.length > 0 ? (
              <TransactionRecord type={transactionType} />
            ) : (
              <Box sx={{ textAlign: 'center', py: 4, color: 'rgba(255, 255, 255, 0.5)', px: 1 }}>
                <ReceiptLongIcon sx={{ fontSize: 36, color: 'rgba(255, 255, 255, 0.2)', mb: 1 }} />
                <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  暂无交易记录
                </Typography>
              </Box>
            )}

            {/* 简洁说明 */}
            <Box sx={{ px: { xs: 1, sm: 0 }, mt: 1 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>
                充值=卡密充值 | 消费=生成小说 | 邀请=好友奖励
              </Typography>
            </Box>
          </Box>
        </TabPanel>
        
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ 
            p: { xs: 1, sm: 2, md: 3 }, 
            maxWidth: 900, 
            mx: 'auto',
            '& > * + *': { mt: { xs: 1.5, sm: 2 } }
          }}>
            {/* 简洁标题栏 */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 1.5, sm: 2 } }}>
              <Typography variant="h6" sx={{ color: 'COLORS.primary', fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                邀请中心
              </Typography>
              <Chip
                label="奖励10%"
                sx={{
                  bgcolor: 'rgba(255, 107, 107, 0.2)',
                  color: '#ff6b6b',
                  border: '1px solid #ff6b6b',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  height: { xs: 24, sm: 26 }
                }}
              />
            </Box>

            {/* 奖励说明 */}
            <Box sx={{ 
              bgcolor: 'rgba(255, 107, 107, 0.1)', 
              border: '1px solid rgba(255, 107, 107, 0.3)',
              borderRadius: 2, 
              p: { xs: 1.5, sm: 2 },
              textAlign: 'center'
            }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: { xs: '0.8rem', sm: '0.9rem' }, lineHeight: 1.6 }}>
                好友每次充值，您可获得其充值金额的
                <Box component="span" sx={{ color: '#ff6b6b', fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.1rem' } }}> 10% </Box>
                奖励
              </Typography>
            </Box>

            {inviteLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} sx={{ color: 'COLORS.primary' }} />
              </Box>
            ) : (
              <>
                {/* 邀请码和链接 */}
                <Box sx={{ 
                  bgcolor: 'rgba(0, 0, 0, 0.3)', 
                  borderRadius: 2, 
                  p: { xs: 1.5, sm: 2 },
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <TextField
                      sx={{ flex: 1 }}
                      size="small"
                      value={inviteCode}
                      label="邀请码"
                      InputProps={{ readOnly: true, sx: { color: '#fff', fontSize: { xs: '0.8rem', sm: '0.875rem' } } }}
                      className="cyber-input"
                    />
                    <Button
                      variant="outlined"
                      onClick={copyInviteCode}
                      startIcon={<ContentCopyIcon />}
                      className="workspace-btn-neon-cyan"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 2, sm: 3 } }}
                    >
                      复制
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <TextField
                      sx={{ flex: 1 }}
                      size="small"
                      value={inviteLink}
                      label="邀请链接"
                      InputProps={{ readOnly: true, sx: { color: '#fff', fontSize: { xs: '0.8rem', sm: '0.875rem' } } }}
                      className="cyber-input"
                    />
                    <Button
                      variant="outlined"
                      onClick={copyInviteLink}
                      startIcon={<ContentCopyIcon />}
                      className="workspace-btn-neon-cyan"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 2, sm: 3 } }}
                    >
                      复制
                    </Button>
                  </Box>
                </Box>

                {/* 邀请统计 - 2x2网格 */}
                {inviteStats && (
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
                    gap: 1.5
                  }}>
                    {[
                      { label: '总邀请', value: inviteStats?.totalInvited },
                      { label: '已激活', value: inviteStats?.activeInvited },
                      { label: '累计奖励', value: inviteStats?.totalReward, suffix: '字' },
                      { label: '累计充值', value: inviteStats?.totalRechargeAmount, suffix: '字' }
                    ].map((item, i) => (
                      <Box key={i} sx={{ 
                        bgcolor: 'rgba(0, 0, 0, 0.3)', 
                        borderRadius: 1.5, 
                        p: { xs: 1.5, sm: 2 },
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        textAlign: 'center'
                      }}>
                        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: { xs: '0.65rem', sm: '0.75rem' }, mb: 0.5 }}>
                          {item.label}
                        </Typography>
                        <Typography sx={{ color: 'COLORS.primary', fontWeight: 'bold', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                          {item.value?.toLocaleString() || 0}{item.suffix || ''}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* 邀请列表 */}
                <Box sx={{ 
                  bgcolor: 'rgba(0, 0, 0, 0.3)', 
                  borderRadius: 2, 
                  p: { xs: 1.5, sm: 2 },
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <Typography variant="subtitle2" sx={{ color: 'COLORS.primary', fontWeight: 'bold', mb: 1.5, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                    邀请列表
                  </Typography>
                  {inviteList.length === 0 ? (
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center', py: 3, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      暂无邀请记录
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {inviteList.slice(0, 10).map((invite, index) => (
                        <Box key={invite._id || index} sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: { xs: 1, sm: 1.5 },
                          bgcolor: 'rgba(0, 0, 0, 0.2)',
                          borderRadius: 1,
                          fontSize: { xs: '0.75rem', sm: '0.8rem' }
                        }}>
                          <Box>
                            <Typography sx={{ color: '#fff', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                              {invite.username || '用户'}
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                              注册: {new Date(invite.createdAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ color: invite.isActive ? 'COLORS.primary' : 'rgba(255,255,255,0.5)', fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                              {invite.isActive ? '已激活' : '未激活'}
                            </Typography>
                            {invite.totalRechargeAmount > 0 && (
                              <Typography sx={{ color: '#ffd700', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                充值: {invite.totalRechargeAmount.toLocaleString()}字
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>
        </TabPanel>
      </Box>

      {/* 修改密码对话框 */}
      <Dialog 
        open={openPasswordDialog} 
        onClose={() => setOpenPasswordDialog(false)}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 30, 60, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid COLORS.primary, 0.2)',
            borderRadius: '16px',
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid COLORS.primary, 0.1)', color: 'primary.main' }}>
          修改密码
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
            <TextField
              label="当前密码"
              variant="outlined"
                      className="workspace-btn-neon-cyan"
              type="text"
              fullWidth
              margin="normal"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              placeholder="请输入当前密码"
            sx={{ 
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': {
                  borderColor: 'COLORS.primary, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'COLORS.primary, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'COLORS.primary',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .Mui-focused.MuiInputLabel-root': {
                color: 'COLORS.primary',
              },
            }}
          />
            <TextField
              label="新密码"
              variant="outlined"
                      className="workspace-btn-neon-cyan"
              type="text"
              fullWidth
              margin="normal"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="请输入新密码（至少6位）"
            sx={{ 
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': {
                  borderColor: 'COLORS.primary, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'COLORS.primary, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'COLORS.primary',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .Mui-focused.MuiInputLabel-root': {
                color: 'COLORS.primary',
              },
            }}
          />
            <TextField
              label="确认新密码"
              variant="outlined"
                      className="workspace-btn-neon-cyan"
              type="text"
              fullWidth
              margin="normal"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="请再次输入新密码"
              error={!!(confirmPassword && newPassword !== confirmPassword)}
            helperText={confirmPassword && newPassword !== confirmPassword ? '两次输入的密码不一致' : ''}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': {
                  borderColor: 'COLORS.primary, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'COLORS.primary, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'COLORS.primary',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .Mui-focused.MuiInputLabel-root': {
                color: 'COLORS.primary',
              },
              '& .MuiFormHelperText-root': {
                color: '#ff5252',
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', borderTop: '1px solid COLORS.primary, 0.1)' }}>
          <Button 
            onClick={() => setOpenPasswordDialog(false)} 
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            取消
          </Button>
          <Button 
            onClick={handleChangePassword} 
            variant="contained" 
            color="primary"
            disabled={!oldPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
            sx={{ 
              textTransform: 'none', 
              borderRadius: '8px',
              bgcolor: 'COLORS.primary',
              color: '#001e3c',
              '&:hover': {
                bgcolor: '#00e699',
              },
            }}
          >
            确认修改
          </Button>
        </DialogActions>
      </Dialog>

      {/* 客服联系方式弹窗 */}
      <Dialog
        open={serviceDialogOpen}
        onClose={() => setServiceDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(15, 23, 42, 0.98)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(34, 211, 238, 0.3)',
            borderRadius: '12px',
          }
        }}
      >
        <DialogTitle sx={{
          color: '#22d3ee',
          fontWeight: 'bold',
          fontSize: '1.1rem',
          textAlign: 'center',
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1
        }}>
          <HeadsetMicIcon sx={{ fontSize: 20 }} />
          联系客服购买卡号
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pt: 1 }}>
          <Typography sx={{ color: 'rgba(224, 242, 254, 0.7)', fontSize: '0.85rem', mb: 2 }}>
            请添加客服微信，咨询购买卡号
          </Typography>
          <Box
            sx={{
              p: 3,
              borderRadius: '10px',
              bgcolor: 'rgba(34, 211, 238, 0.08)',
              border: '1px solid rgba(34, 211, 238, 0.2)',
              mb: 2
            }}
          >
            <Typography sx={{ color: 'rgba(224, 242, 254, 0.5)', fontSize: '0.7rem', mb: 1 }}>
              客服微信号
            </Typography>
            <Typography sx={{
              color: '#22d3ee',
              fontSize: '1.3rem',
              fontWeight: 'bold',
              fontFamily: 'monospace',
              letterSpacing: '2px'
            }}>
              {serviceWechatId}
            </Typography>
          </Box>
          <Button
            variant="contained"
            fullWidth
            onClick={copyWechatId}
            className="workspace-btn-neon-cyan"
            sx={{
              fontWeight: 'bold',
              py: 1.2,
            }}
          >
            复制微信号
          </Button>
          <Typography sx={{ color: 'rgba(224, 242, 254, 0.4)', fontSize: '0.7rem', mt: 2 }}>
            复制后打开微信搜索添加
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Snackbar提示 - 用于成功提示和其他非错误提示 */}
      {snackbar.open && snackbar.severity !== 'error' && (
        <Snackbar
          key={snackbarKey}
          open={snackbar.open}
          autoHideDuration={snackbar.autoHideDuration || 6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{
            top: '100px !important',
            zIndex: 9999
          }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default UserCenter; 